import { FormError, Formheader, GrayBox } from "@/Components";
import { LabeledInput, LabeledTextarea } from "@/Components/Form";
import React, { useEffect, useState } from "react";
import { usePayload } from "@/Contexts/PayloadContext";
import { usePage } from "@inertiajs/inertia-react";
import { IconFileUpload } from "@/Icons";
import FileUpload from "@/Components/FileUpload";
import { FileText } from "react-feather";

export default function AgencyFormPart({ organization = null }) {
  const { payload, setPayload, onFormChange } = usePayload();

  const [showFileUpload, setShowFileUpload] = useState(false);

  const errors = usePage().props?.errors || {};

  useEffect(() => {
    if (organization) {
      setPayload(organization);
    }
  }, []);

  useEffect(() => {
    if (organization) {
      setPayload((prev) => ({
        ...prev,
        associated_files: organization.associated_files,
      }));
    }
  }, [organization]);

  const handleFileAdded = (e) => {
    const file = e.target.files[0];
    console.log(file);

    // Append the new file to the existing list of files in the payload
    setPayload((prev) => ({
      ...prev,
      new_files: [...(prev?.new_files || []), file],
    }));
  };

  const File = ({ file, exists = true }) => {
    return (
      <div className="flex w-full items-center justify-between gap-4 bg-white p-4">
        <div className="flex items-center gap-2">
          <FileText />
          {exists && (
            <a
              href={file.url}
              download
              target="_blank"
              className="underline hover:no-underline"
            >
              {file.name}
            </a>
          )}
          {!exists && <span>{file.name}</span>}
        </div>
        <button
          type="button"
          className="underline hover:no-underline"
          onClick={() => {
            setPayload((prev) => {
              const updatedPayload = { ...prev };

              if (exists) {
                updatedPayload.removed_files = [
                  ...(prev.removed_files || []),
                  file.path,
                ];
                updatedPayload.associated_files = prev.associated_files.filter(
                  (f) => f !== file,
                );
              } else {
                updatedPayload.new_files = prev.new_files.filter(
                  (f) => f !== file,
                );
              }

              return updatedPayload;
            });
          }}
        >
          Delete
        </button>
      </div>
    );
  };

  return (
    <>
      <GrayBox margin="mb-4">
        <div className="flex flex-wrap gap-4">
          <Formheader margin="w-full">Organization details</Formheader>
          <LabeledInput
            required
            title="Name of organization"
            name="name"
            onChange={onFormChange}
            containerClass="w-full sm:basis-1/2-gap-4"
            placeholder="Enter name of organization"
            value={payload?.name || ""}
            error={errors?.name}
          />
          <LabeledInput
            required
            title="Organization number"
            name="organization_number"
            onChange={onFormChange}
            containerClass="w-full sm:basis-1/2-gap-4"
            placeholder="Enter organization number"
            value={payload?.organization_number || ""}
            error={errors?.organization_number}
          />
          <LabeledInput
            required
            title="Contact e-mail"
            name="email"
            type="email"
            onChange={onFormChange}
            containerClass="w-full sm:basis-1/2-gap-4"
            placeholder="Enter contact e-mail"
            value={payload?.email || ""}
            error={errors?.email}
          />
          <LabeledInput
            required
            title="Contact phone number"
            name="phone"
            onChange={onFormChange}
            containerClass="w-full sm:basis-1/2-gap-4"
            placeholder="Enter contact phone number"
            value={payload?.phone || ""}
            error={errors?.phone}
          />
          <LabeledTextarea
            title="Comment"
            name="comment"
            containerClass="w-full"
            onChange={onFormChange}
            value={payload?.comment || ""}
            error={errors?.comment}
          />
        </div>
      </GrayBox>
      <GrayBox margin="mb-4">
        <div className="flex flex-wrap gap-4">
          <Formheader margin="w-full">Owner details</Formheader>
          <LabeledInput
            required
            title="First name"
            name="firstname"
            onChange={onFormChange}
            containerClass="w-full sm:basis-1/2-gap-4"
            placeholder="Enter first name"
            value={payload?.firstname || ""}
            error={errors?.firstname}
          />
          <LabeledInput
            required
            title="Surname"
            name="surname"
            onChange={onFormChange}
            containerClass="w-full sm:basis-1/2-gap-4"
            placeholder="Enter surname"
            value={payload?.surname || ""}
            error={errors?.surname}
          />
          <LabeledInput
            required
            title="E-mail"
            name="user_email"
            onChange={onFormChange}
            containerClass="w-full"
            placeholder="Enter e-mail"
            type="email"
            value={payload?.user_email || ""}
            error={errors?.user_email}
          />
        </div>
      </GrayBox>
      <GrayBox margin="mb-4">
        <div className="flex flex-wrap gap-4">
          <Formheader margin="w-full">Billing details</Formheader>
          <div className="w-full">
            <label className="mr-4">
              <input
                checked={payload?.billing_method === "email"}
                value={"email"}
                type="radio"
                name="billing_method"
                onChange={onFormChange}
              />
              <span className="ml-2">Billing by e-mail</span>
            </label>

            <label className="mr-4">
              <input
                checked={payload?.billing_method === "postal"}
                value={"postal"}
                type="radio"
                name="billing_method"
                onChange={onFormChange}
              />
              <span className="ml-2">
                Billing by snail-mail (0,54 gram CO₂e per bill)
              </span>
            </label>
            <FormError error={errors?.billing_method} />
          </div>

          {payload?.billing_method === "email" && (
            <>
              <LabeledInput
                title="E-mail"
                placeholder="Enter e-mail"
                name="billing_email"
                required
                type="email"
                value={payload?.billing_email || ""}
                onChange={onFormChange}
                error={errors?.billing_email}
                containerClass="w-full"
              />
            </>
          )}

          {payload?.billing_method === "postal" && (
            <>
              <LabeledInput
                title="Billing address"
                placeholder="Enter billing address"
                name="billing_address"
                required
                value={payload?.billing_address || ""}
                onChange={onFormChange}
                error={errors?.billing_address}
                containerClass="w-full"
              />

              <LabeledInput
                title="ZIP-code"
                placeholder="Enter ZIP-code"
                name="billing_zipcode"
                required
                value={payload?.billing_zipcode || ""}
                onChange={onFormChange}
                error={errors?.billing_zipcode}
                containerClass="w-full sm:basis-1/2-gap-4"
              />

              <LabeledInput
                title="City"
                placeholder="Enter city"
                name="billing_city"
                required
                value={payload?.billing_city || ""}
                onChange={onFormChange}
                error={errors?.billing_city}
                containerClass="w-full sm:basis-1/2-gap-4"
              />
            </>
          )}

          <LabeledInput
            title="License fee"
            placeholder="Enter price"
            name="billing_monthly_price"
            value={payload?.billing_monthly_price || ""}
            onChange={onFormChange}
            error={errors?.billing_monthly_price}
            containerClass="w-full sm:basis-1/2-gap-4"
          />

          <LabeledInput
            title="Price per global reporter"
            placeholder="Enter price"
            name="billing_global_reporter_price"
            value={payload?.billing_global_reporter_price || ""}
            onChange={onFormChange}
            error={errors?.billing_global_reporter_price}
            containerClass="w-full sm:basis-1/2-gap-4"
          />

          <LabeledInput
            title="Country"
            placeholder="Enter country"
            name="billing_country"
            required
            value={payload?.billing_country || ""}
            onChange={onFormChange}
            error={errors?.billing_country}
            containerClass="w-full sm:basis-1/2-gap-4"
          />

          <LabeledInput
            title="VAT-number"
            placeholder="Enter VAT-number if any"
            name="vat_number"
            value={payload?.vat_number || ""}
            onChange={onFormChange}
            error={errors?.vat_number}
            containerClass="w-full sm:basis-1/2-gap-4"
          />
        </div>
      </GrayBox>
      <GrayBox margin="mb-4">
        <div className="flex flex-wrap gap-4">
          <Formheader margin="w-full">Files</Formheader>

          <div className="flex w-full flex-col gap-2">
            {payload?.associated_files?.map((file, index) => (
              <File key={index} file={file} />
            ))}
            {payload?.new_files?.map((file, index) => (
              <File key={index} file={file} exists={false} />
            ))}
          </div>

          {!showFileUpload && (
            <button
              className="flex items-center gap-1 underline hover:no-underline"
              onClick={() => setShowFileUpload(true)}
              type="button"
            >
              <IconFileUpload />
              Upload document
            </button>
          )}

          {showFileUpload && (
            <>
              <FileUpload
                onFileAdded={handleFileAdded}
                supportedFileTypes={{
                  pdf: { meme: "application/pdf" },
                  docx: {
                    meme: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  },
                  doc: { meme: "application/msword" },
                }}
                maxFileSize={20480}
              />
              <button
                className="underline hover:no-underline"
                onClick={() => setShowFileUpload(false)}
                type="button"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </GrayBox>
    </>
  );
}
